import {
    Box,
    Link,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Stack,
    Typography,
    type SxProps,
    type Theme,
} from '@mui/material';
import { Plus, Sparkles } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Features } from '~/components/FeatureToggle';
import useAvailableStructureSets from '~/hooks/structure-set/useAvailableStructureSets';
import useCreateStructureSet from '~/hooks/structure-set/useCreateStructureSet';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { WorkflowTypes, workflowModalState } from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import StructureSetSelection from './StructureSetSelection';

const actionMenuItemSx: SxProps<Theme> = {
    px: 1,
    py: 1,
    borderRadius: 0,
    bgcolor: 'rgba(255, 255, 255, 0.02)',
    '&:hover': {
        bgcolor: 'rgba(144, 202, 249, 0.08)',
    },
};

const actionMenuIconSx: SxProps<Theme> = {
    color: 'primary.main',
    minWidth: 28,
};

export default function EmptyStructureSet() {
    const createEmptyStructureSet = useCreateStructureSet();
    const availableStructureSets = useAvailableStructureSets(true);
    const setWorkflowModal = useSetRecoilState(workflowModalState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const primaryImage = useRecoilValue(primaryImageState);
    const isContourWorkflowEnabled = AppConfig.isFeatureEnabled(
        Features.CONTOUR_WORKFLOW
    );
    const isGenerateDisabled = !selectedPatientId || !primaryImage;

    const handleGenerateStructureSet = () => {
        setWorkflowModal((state) => ({
            ...state,
            open: true,
            page: WorkflowTypes.CONTOUR,
        }));
    };

    return (
        <Stack display="flex" flexDirection="column">
            <StructureSetSelection />
            {availableStructureSets.length > 0 ? (
                <Box>
                    {isContourWorkflowEnabled && (
                        <MenuItem
                            onClick={handleGenerateStructureSet}
                            disabled={isGenerateDisabled}
                            data-testid="generate-structureset-button"
                            sx={actionMenuItemSx}
                        >
                            <ListItemIcon sx={actionMenuIconSx}>
                                <Sparkles size={13} />
                            </ListItemIcon>
                            <ListItemText
                                primaryTypographyProps={{
                                    fontSize: 12,
                                }}
                            >
                                <FormattedMessage id="generateStructureSet" />
                            </ListItemText>
                        </MenuItem>
                    )}
                    <MenuItem
                        onClick={() => {
                            createEmptyStructureSet();
                        }}
                        data-testid="create-structure-set-button"
                        sx={actionMenuItemSx}
                    >
                        <ListItemIcon sx={actionMenuIconSx}>
                            <Plus size={14} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{
                                fontSize: 12,
                            }}
                        >
                            <FormattedMessage id="createStructureSet" />
                        </ListItemText>
                    </MenuItem>
                </Box>
            ) : (
                <Box px={1.5} mb={2}>
                    <Typography fontSize={12} color="text.secondary">
                        <FormattedMessage id="workflow.contouring.noStructureSets" />{' '}
                        <Link
                            component="button"
                            fontSize={12}
                            onClick={() => {
                                createEmptyStructureSet();
                            }}
                            sx={{
                                verticalAlign: 'baseline',
                                cursor: 'pointer',
                            }}
                            data-testid="create-structure-set-button"
                        >
                            <FormattedMessage id="workflow.contouring.createNewStructureSet" />
                        </Link>
                    </Typography>
                </Box>
            )}
        </Stack>
    );
}
